import React, { useState, useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Box, Typography, makeStyles, useMediaQuery } from "@material-ui/core";
import _ from "lodash";
import getFilteredProductList from "lib/filters/products";
import { primaryColor } from "resources/constants";
import ScenarioTotalsCardComponent from "app/scenarios/map/ScenarioTotalsCardComponent";
import ScaleLoader from "react-spinners/ScaleLoader";
import {
  reduceEstimations,
  filterEstimations,
  groupEstimationsByKeyAndProduct,
} from "lib/filters/estimations";
import { computeEstimationTotals } from "lib/utils/estimations";
import computeScenarioTotalsForCard from "lib/utils/scenarios";
import { compareKeys } from "lib/utils";
import { setFilters } from "app/filters/duck/slices";
import { setCurrentView } from "app/common/view/duck/slices";
import mixpanelTrack from "app/common/MixpanelComponent";
import { BsTable, BsBarChart, BsQuestionCircle } from "react-icons/bs";
import Tab from "app/common/Tab";
import Tabs from "app/common/Tabs";
import getDefaultCycle, { getCycleTypeId } from "lib/utils/cycles";
import { useLocation } from "react-router-dom";
import GroupByComponent from "app/filters/GroupByComponent";
import { changeSelectedScenarioAsyncAction } from "app/scenarios/duck/actions";
import CannibalizationTooltipComponent from "app/cannibalization/CannibalizationTooltipComponent";
import CannibalizationToggleComponent from "app/cannibalization/CannibalizationToggleComponent";
import EstimationsGroupComponent from "./EstimationsGroupComponent";
import EstimationsListComponent from "./EstimationsListComponent";
import SummaryComponent from "../summary/SummaryComponent";
import ExplicabilityComponent from "../explicability-info/ExplicabilityComponent";

const useStyles = makeStyles((theme) => ({
  tableContainer: {
    margin: "0px 20px 0px 20px",
    flexGrow: 1,
    overflow: "auto",
    overflowY: "hidden",
    [theme.breakpoints.down("sm")]: {
      height: "60vh",
    },
    [theme.breakpoints.up("md")]: {
      height: "75vh",
    },
    [theme.breakpoints.up("lg")]: {
      height: "100%",
    },
  },
  totalsContainer: {
    display: "flex",
    justifyContent: "space-between",
    gap: "5px",
    paddingBottom: "8px",
    borderBottom: "1px solid #e2e2e2",
    [theme.breakpoints.down(800)]: {
      flexDirection: "column",
    },
  },
  spinnerContainer: {
    display: "flex",
    flexGrow: "1",
    alignItems: "center",
    justifyContent: "center",
  },
  spinnerContainerMessage: {
    display: "flex",
    flexGrow: "1",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "row",
  },
  iconButton: {
    border: "1px solid #e2e2e2",
    borderRadius: "5px",
  },
  groupByContainer: {
    [theme.breakpoints.down("sm")]: {
      padding: "10px 0px",
      marginLeft: "5px",
      marginRight: "5px",
    },
    [theme.breakpoints.up("md")]: {
      marginLeft: "25px",
      marginRight: "25px",
    },
  },
  tableHeadingContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "10px 0px",
  },
  sectionTitle: {
    display: "flex",
    alignItems: "center",
    width: "auto",

    [theme.breakpoints.up("md")]: {
      fontSize: "2vw",
    },
    [theme.breakpoints.up("lg")]: {
      fontSize: "1.5vw",
    },
    [theme.breakpoints.up(1600)]: {
      fontSize: "1.2vw",
    },
  },
  sectionMessage: {
    color: "#C0C0C0",
    display: "flex",
  },
  sectionMessageSmall: {
    color: "#C0C0C0",
    display: "flex",
    fontSize: "0.7rem",
  },
  textColorBlack: {
    color: "black",
    marginLeft: "4px",
  },
  sectionIndicatorContainer: {
    display: "flex",
    alignItems: "center",
    gap: "5px",
    [theme.breakpoints.down(800)]: {
      justifyContent: "space-between",
    },
  },
}));

export default function EstimationsParentComponent() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const location = useLocation();
  const scenariosSlice = useSelector((state) => state.scenarios);
  const metadataSlice = useSelector((state) => state.metadata);
  const filtersSlice = useSelector((state) => state.filters);
  const retailersGroupSlice = useSelector((state) => state.retailersGroup);
  const catalogSlice = useSelector((state) => state.catalog);
  const cycleSlice = useSelector((state) => state.cycle);
  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down("md"));
  const retailersSlice = useSelector((state) => state.retailers);
  const areasSlice = useSelector((state) => state.areas);
  const viewSlice = useSelector((state) => state.view);
  const configCatSlice = useSelector((state) => state.auth.configCatUser);
  const [visibleScenarioEstimations, setVisibleScenarioEstimations] = useState(
    []
  );
  const [
    visibleScenarioEstimationsByProduct,
    setVisibleScenarioEstimationsByProduct,
  ] = useState([]);
  const [visibleScenarioTotals, setVisibleScenarioTotals] = useState({
    selectedScenario: {},
    referenceScenario: {},
  });

  const isPricing = window.location.pathname.includes("pricing");

  const getExpansionPanelsForGroupBy = () => {
    switch (filtersSlice.groupBy.key) {
      case "product":
        return (
          <div className={classes.tableContainer}>
            <EstimationsListComponent
              key="product"
              groupBy="product"
              referenceEstimations={
                visibleScenarioEstimationsByProduct.referenceScenario
              }
              scenarioEstimations={
                visibleScenarioEstimationsByProduct.selectedScenario
              }
            />
          </div>
        );
      case "retailer":
        return (
          <EstimationsGroupComponent
            key="retailer"
            groupBy="retailer"
            itemList={retailersSlice}
            filters={filtersSlice.filters}
            referenceEstimations={visibleScenarioEstimations.referenceScenario}
            scenarioEstimations={visibleScenarioEstimations.selectedScenario}
            metadata={metadataSlice["custom-projection"]}
          />
        );
      case "retailerGroup":
        return (
          <EstimationsGroupComponent
            key="retailerGroup"
            groupBy="retailerGroup"
            itemList={retailersGroupSlice}
            filters={filtersSlice.filters}
            referenceEstimations={visibleScenarioEstimations.referenceScenario}
            scenarioEstimations={visibleScenarioEstimations.selectedScenario}
            metadata={metadataSlice["custom-projection"]}
          />
        );
      case "area":
        return (
          <EstimationsGroupComponent
            key="area"
            groupBy="area"
            itemList={areasSlice}
            filters={filtersSlice.filters}
            referenceEstimations={visibleScenarioEstimations.referenceScenario}
            scenarioEstimations={visibleScenarioEstimations.selectedScenario}
            metadata={metadataSlice["custom-projection"]}
          />
        );
      default:
        return (
          <div className={classes.tableContainer}>
            <EstimationsListComponent
              key="independent"
              groupBy={false}
              referenceEstimations={
                visibleScenarioEstimations.referenceScenario
              }
              scenarioEstimations={visibleScenarioEstimations.selectedScenario}
            />
          </div>
        );
    }
  };

  useMemo(() => {
    if (
      _.isEmpty(scenariosSlice.selectedScenario.estimations) ||
      !compareKeys(
        scenariosSlice.selectedScenario.estimations,
        scenariosSlice.referenceScenario.estimations
      )
    )
      return;

    const filteredProducts = _.map(
      getFilteredProductList(filtersSlice.filters, catalogSlice.products),
      (product) => product.id
    );

    const filteredSelectedScenarioEstimations = filterEstimations(
      scenariosSlice.selectedScenario.estimations,
      filteredProducts,
      filtersSlice.filters,
      filtersSlice.groupBy.key
    );

    const filteredReferenceScenarioEstimations = filterEstimations(
      scenariosSlice.referenceScenario.estimations,
      filteredProducts,
      filtersSlice.filters,
      filtersSlice.groupBy.key
    );

    if (filtersSlice.groupBy.key === "product") {
      const filteredSelectedScenarioEstimationsByProduct = reduceEstimations(
        groupEstimationsByKeyAndProduct(
          filteredSelectedScenarioEstimations,
          "product"
        ),
        "product",
        metadataSlice["custom-projection"],
        scenariosSlice.referenceScenario.estimations,
        configCatSlice?.features.newInvestmentCalculation
      );
      const filteredReferenceScenarioEstimationsByProduct = reduceEstimations(
        groupEstimationsByKeyAndProduct(
          filteredReferenceScenarioEstimations,
          "product"
        ),
        "product",
        metadataSlice["custom-projection"],
        configCatSlice?.features.newInvestmentCalculation
      );
      setVisibleScenarioEstimationsByProduct({
        selectedScenario: filteredSelectedScenarioEstimationsByProduct,
        referenceScenario: filteredReferenceScenarioEstimationsByProduct,
      });
    }

    setVisibleScenarioEstimations({
      selectedScenario: filteredSelectedScenarioEstimations,
      referenceScenario: filteredReferenceScenarioEstimations,
    });

    setVisibleScenarioTotals({
      selectedScenario: computeEstimationTotals(
        filteredSelectedScenarioEstimations,
        filteredReferenceScenarioEstimations,
        configCatSlice?.features.newInvestmentCalculation,
        configCatSlice?.features.roiStrategy
      ),
      referenceScenario: computeEstimationTotals(
        filteredReferenceScenarioEstimations,
        filteredReferenceScenarioEstimations,
        configCatSlice?.features.newInvestmentCalculation,
        configCatSlice?.features.roiStrategy
      ),
    });
  }, [
    scenariosSlice.selectedScenario.estimations,
    scenariosSlice.referenceScenario.estimations,
    filtersSlice.filters,
  ]);

  useEffect(() => {
    if (filtersSlice.groupBy.key === "area") {
      dispatch(
        setFilters({
          ...filtersSlice.filters,
          retailer: "",
          retailerGroup: "",
        })
      );
    }
    if (filtersSlice.groupBy.key === "retailer") {
      dispatch(
        setFilters({
          ...filtersSlice.filters,
          area: "",
        })
      );
    }
    if (filtersSlice.groupBy.key === "product") {
      dispatch(
        setFilters({
          ...filtersSlice.filters,
          area: "",
          retailer: "",
          retailerGroup: "",
        })
      );
    }
  }, [filtersSlice.groupBy]);

  React.useEffect(() => {
    /* MIXPANEL TRACKS */
    mixpanelTrack("Promotional grid module visited");
    /* END OF MIXPANEL TRACKS */
  }, []);

  const cycleByLocation = location.pathname.includes("Pricing")
    ? "Pricing"
    : "Trade Promotions";

  const messageForEmptySelectedScenario = (
    <Box component="div" className={classes.spinnerContainerMessage}>
      <Typography
        variant="h6"
        className={
          isSmallScreen ? classes.sectionMessageSmall : classes.sectionMessage
        }
      >
        There are no promotional cycles uploaded in this
        <div className={classes.textColorBlack}>TPo section</div>
      </Typography>
    </Box>
  );

  const cycleTypeListData = _.filter(cycleSlice.list, (cycle) => {
    return (
      cycle.cycle_type_id === getCycleTypeId(cycleByLocation, cycleSlice)[0]?.id
    );
  });

  React.useEffect(() => {
    if (
      _.isEmpty(scenariosSlice.selectedScenario) &&
      !_.isEmpty(cycleTypeListData)
    ) {
      const closestFutureCycle = getDefaultCycle(cycleTypeListData);

      const initialSelectedScenarioTotals = _.find(
        scenariosSlice.totalsByScenarioId,
        (scenarioTotal) =>
          scenarioTotal.scenario.scenario_type_id === 1 &&
          scenarioTotal.scenario.cycle_id === closestFutureCycle
      );
      dispatch(
        changeSelectedScenarioAsyncAction(
          initialSelectedScenarioTotals.scenario.id
        )
      );
    }
  }, [location.pathname]);

  // This property should change in the future
  // how should we detect if the client has cannibalization enabled
  const cannibalizationExists = true;
  // Extract the property that states if the scenario is currently cannibalized or not
  const isBlocked = scenariosSlice.selectedScenario?.scenario?.is_blocked;

  const checkSeletedScenarioData = _.isEmpty(
    scenariosSlice.selectedScenario.estimations
  ) ? (
    messageForEmptySelectedScenario
  ) : (
    <>
      <Box className={classes.totalsContainer}>
        <div className={classes.sectionIndicatorContainer}>
          {isPricing ? (
            <Typography variant="h6" className={classes.sectionTitle}>
              {viewSlice === "grid"
                ? `Pricing: ${isBlocked ? "Cannibalized" : ""} Grid`
                : `Pricing: ${isBlocked ? "Cannibalized" : ""} Summary`}
              {isBlocked && (
                <CannibalizationTooltipComponent
                  iconComponent={<BsQuestionCircle fontSize={14} />}
                />
              )}
              {viewSlice === "grid" ? null : (
                <ExplicabilityComponent tooltipText="Compare your custom pricing escenario with your reference. See the consequences of your decisions in your Margin, Volume and KPIs" />
              )}
            </Typography>
          ) : (
            <Typography variant="h6" className={classes.sectionTitle}>
              {viewSlice === "grid"
                ? `TPo: ${isBlocked ? "Cannibalized" : ""} Grid`
                : `TPo: ${isBlocked ? "Cannibalized" : ""} Summary`}
              {isBlocked && (
                <CannibalizationTooltipComponent
                  iconComponent={<BsQuestionCircle fontSize={14} />}
                />
              )}
              {viewSlice === "grid" ? null : (
                <ExplicabilityComponent tooltipText="Compare your custom TPo escenario with your reference. See the consequences of your decisions in your Margin, Volume and KPIs" />
              )}
            </Typography>
          )}
          <ViewTabsComponent />
        </div>
        {!_.isEmpty(visibleScenarioTotals.selectedScenario) && (
          <ScenarioTotalsCardComponent
            row
            showLabels
            isReference={
              scenariosSlice.selectedScenario.scenario.scenario_type_id === 1
            }
            cardData={computeScenarioTotalsForCard(
              visibleScenarioTotals.selectedScenario,
              visibleScenarioTotals.referenceScenario
            )}
            orderWithVisibleKeys
            visibleKeys={[
              "price",
              "units",
              "sales",
              "margin",
              "roi",
              "investment_in",
            ]}
            metadata={metadataSlice}
          />
        )}
      </Box>
      {/* Render scenario estimations table */}
      {viewSlice === "grid" && (
        <Box
          style={{
            height: "100%",
          }}
        >
          <Box
            style={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              overflow: "auto",
            }}
          >
            <Box className={classes.groupByContainer}>
              <div className={classes.tableHeadingContainer}>
                <GroupByComponent />
                {cannibalizationExists && <CannibalizationToggleComponent />}
              </div>
            </Box>
            {getExpansionPanelsForGroupBy()}
          </Box>
        </Box>
      )}
      {/* Render scenario summary */}
      {viewSlice === "summary" && (
        <Box
          style={{
            height: "100%",
          }}
        >
          <SummaryComponent
            scenarioEstimations={visibleScenarioEstimations}
            isReference={
              scenariosSlice.selectedScenario.scenario.scenario_type_id === 1
            }
            scenarioTotals={visibleScenarioTotals}
          />
        </Box>
      )}
    </>
  );

  return scenariosSlice.loadingScenario ? (
    <Box component="div" className={classes.spinnerContainer}>
      <ScaleLoader size={150} color={primaryColor} loading />
    </Box>
  ) : (
    checkSeletedScenarioData
  );
}

const ViewTabsComponent = () => {
  const dispatch = useDispatch();
  const viewSlice = useSelector((state) => state.view);

  const handleTabChange = (event, newValue) => {
    dispatch(setCurrentView(newValue));
  };

  return (
    <Box>
      <Tabs
        style={{ maxHeight: "60px", alignItems: "center" }}
        value={viewSlice}
        onChange={handleTabChange}
        aria-label="custom tabs example"
        variant="scrollable"
        scrollButtons="off"
      >
        <Tab
          style={{ width: "80px" }}
          value="grid"
          label={<div style={{ fontSize: "10px" }}>Grid</div>}
          disableRipple
          aria-label="table"
          icon={<BsTable size={18} />}
        />
        <Tab
          style={{ width: "80px" }}
          value="summary"
          label={<div style={{ fontSize: "10px" }}>Summary</div>}
          disableRipple
          icon={<BsBarChart size={18} />}
          aria-label="dashboard"
        />
      </Tabs>
    </Box>
  );
};
